import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';

enum RetryHttpStatus {
  RateLimit = 429,
  ClientErrorMin = 400,
  ServerErrorMin = 500,
}

const DEFAULT_MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 1000;

interface ApiClientRetryRequestConfigLocal extends AxiosRequestConfig {
  _retry?: number;
  _noRetry?: boolean;
}

function getResponseStatus(error: AxiosError): number | null {
  return error.response?.status ?? null;
}

function isTerminalClientErrorStatus(status: number): boolean {
  return (
    status >= RetryHttpStatus.ClientErrorMin &&
    status < RetryHttpStatus.ServerErrorMin &&
    status !== RetryHttpStatus.RateLimit
  );
}

function isRetryableStatus(status: number | null): boolean {
  return (
    status == null ||
    status >= RetryHttpStatus.ServerErrorMin ||
    status === RetryHttpStatus.RateLimit
  );
}

function shouldRetryRequest(input: {
  config: ApiClientRetryRequestConfigLocal;
  error: AxiosError;
}): boolean {
  const retryCount = input.config._retry ?? 0;
  if (retryCount >= DEFAULT_MAX_RETRIES) {
    return false;
  }

  return isRetryableStatus(getResponseStatus(input.error));
}

function shouldRejectWithoutRetry(input: {
  config: ApiClientRetryRequestConfigLocal;
  error: AxiosError;
}): boolean {
  if (input.config._noRetry) {
    return true;
  }

  const status = getResponseStatus(input.error);
  return status != null && isTerminalClientErrorStatus(status);
}

function getRetryDelay(retryCount: number): number {
  return RETRY_BASE_DELAY_MS * Math.pow(2, retryCount - 1);
}

function waitForRetryDelay(delay: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, delay));
}

function getRetryableRequestConfig(error: AxiosError): ApiClientRetryRequestConfigLocal | null {
  return error.config ? (error.config as ApiClientRetryRequestConfigLocal) : null;
}

function getNextRetryCount(input: {
  config: ApiClientRetryRequestConfigLocal;
  error: AxiosError;
}): number | null {
  if (shouldRejectWithoutRetry(input)) {
    return null;
  }

  if (!shouldRetryRequest(input)) {
    return null;
  }

  return (input.config._retry ?? 0) + 1;
}

function createRetryRequestConfig(input: {
  config: ApiClientRetryRequestConfigLocal;
  nextRetryCount: number;
}): ApiClientRetryRequestConfigLocal {
  return {
    ...input.config,
    _retry: input.nextRetryCount,
  };
}

async function handleRetryableAxiosError(input: {
  axiosInstance: AxiosInstance;
  error: AxiosError;
}): Promise<AxiosResponse<unknown>> {
  const config = getRetryableRequestConfig(input.error);

  if (!config) {
    return Promise.reject(input.error);
  }

  const nextRetryCount = getNextRetryCount({
    config,
    error: input.error,
  });
  if (nextRetryCount == null) {
    return Promise.reject(input.error);
  }

  const retryConfig = createRetryRequestConfig({ config, nextRetryCount });
  await waitForRetryDelay(getRetryDelay(nextRetryCount));
  return input.axiosInstance.request(retryConfig);
}

export function installRetryInterceptor(input: {
  axiosInstance: AxiosInstance;
}): void {
  input.axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) =>
      handleRetryableAxiosError({
        axiosInstance: input.axiosInstance,
        error,
      }),
  );
}
